import { useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { Route, Outlet, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Howl } from "howler";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType, LocalStorageKey } from "app/types";
import { useScale } from "common/utils";
import { HomePage } from "features/home";
import { AuthPageRoutes } from "features/auth";
import { TopTutorial } from "features/tutorial";
import { UniversalBtn } from "features/universal";
import { CoursePageRoutes } from "features/course";
import { DialogPage } from "features/top/DialogPage";
import { UserBtn, UserFullPage } from "features/user";
import { ParentGate } from "features/parent/ParentGate";
import { CreatorTop, CreatorMain } from "features/creator";
import { NotificationDetail } from "features/notification";
import { ParentSettingPage, ReAuthModal } from "features/parent";
import { usePlan, PlanType, TrialNoticeBalloon } from "features/subscription";
import { StartupScreen } from "./StartupScreen";
import { useInitialApp } from "./useInitialApp";
import { useInitialAppConfig } from "./useInitialAppConfig";

export type ConfigContext = {
  bgmSound: Howl;
  audioContext: AudioContext;
};

const ParentGateWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const plan = usePlan();
  const [showReAuth, setShowReAuth] = useState(false);
  const parent = useSelector((state: RootState) => state.user.parent);
  const parentGate = useSelector((state: RootState) => state.config.dialog.parent_gate);

  // noPlayerModeの判定（プレイヤー0人時の強制表示モード）
  const isNoPlayerMode = parentGate.args?.noPlayerMode === "true";

  // URL直接アクセスで未ログインの場合はリダイレクト
  useEffect(() => {
    if (location.pathname === "/parent") {
      if (!parent) {
        navigate("/", { replace: true });
      } else if (parent.account_type === "guest") {
        navigate("/top", { replace: true });
      }
    }
  }, [location.pathname, parent, navigate]);

  // 10分経過チェック
  const checkNeedsReAuth = (): boolean => {
    const lastSignAt = localStorage.getItem(LocalStorageKey.LAST_SIGN_AT);
    if (!lastSignAt) return true;

    const lastTime = dayjs(lastSignAt);
    const now = dayjs();
    return now.diff(lastTime, "minute") >= 10;
  };

  const handleClose = () => {
    // noPlayerModeの場合は閉じられない
    if (isNoPlayerMode) return;
    handleDialog({ type: DialogType.PARENT_GATE, value: false });
  };

  const handleConfirm = () => {
    // noPlayerModeの場合は単にダイアログを閉じる（/parentに既にいる）
    if (isNoPlayerMode) {
      handleDialog({ type: DialogType.PARENT_GATE, value: false });
      return;
    }
    // ログイン済みユーザーで10分以上経過 → 再認証
    if (plan === PlanType.EDUCATION && parent?.email && checkNeedsReAuth()) {
      handleDialog({ type: DialogType.PARENT_GATE, value: false });
      setShowReAuth(true);
      return;
    }
    // それ以外 → 直接遷移
    handleDialog({ type: DialogType.PARENT_GATE, value: false });
    navigate("/parent");
  };

  const handleReAuthSuccess = () => {
    setShowReAuth(false);
    navigate("/parent");
  };

  const handleReAuthClose = () => {
    setShowReAuth(false);
  };

  return (
    <>
      <ParentGate
        visible={parentGate.value}
        close={handleClose}
        confirm={handleConfirm}
        canClose={!isNoPlayerMode}
      />
      <ReAuthModal open={showReAuth} onSuccess={handleReAuthSuccess} onClose={handleReAuthClose} />
    </>
  );
};

const AppLayout = ({ bgmSound, audioContext }: ConfigContext) => {
  return (
    <div className="flex-col-el relative w-full h-full">
      <Outlet context={{ bgmSound, audioContext }} />
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { bgmSound, audioContext } = useInitialAppConfig();
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);
  const transitionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (/\/courses\/[a-zA-Z0-9_]+/.test(location.pathname)) {
        const canvas = document.getElementById("app-canvas") as unknown as HTMLCanvasElement;
        if (canvas) {
          canvas.style.opacity = "0";
        }
      }
    };
  }, [location]);

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={location.key || location.pathname + location.search}
        classNames="fade"
        timeout={400}
        nodeRef={transitionRef}
      >
        <div ref={transitionRef} className="fade">
          <Routes location={location}>
            <Route path="/">
              <Route index element={<StartupScreen />} />

              {/* 保護者ページは parent のみで表示可能（activeなしでもアクセス可能） */}
              {parent && <Route path="/parent" element={<ParentSettingPage />} />}

              {parent && active && (
                <Route
                  path="/"
                  element={<AppLayout bgmSound={bgmSound} audioContext={audioContext} />}
                >
                  <Route path="/top" element={<HomePage />} />
                  <Route path="/courses">{CoursePageRoutes}</Route>
                  <Route path="/user" element={<UserFullPage />} />
                  <Route path="/creator">
                    <Route index element={<CreatorTop />} />
                    <Route path=":id" element={<CreatorMain />} />
                  </Route>
                </Route>
              )}

              <Route path="auth/*" element={<AuthPageRoutes />} />
              <Route path="/*" element={<Navigate to="/" />} />
            </Route>
          </Routes>
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export const AppIndexPage = () => {
  useInitialApp();
  const { scale } = useScale();
  const active = useSelector((state: RootState) => state.user.active);

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        transition={Slide}
        hideProgressBar={true}
        style={{
          zIndex: 99999999999,
          left: "50%",
          transform: `translateX(-50%) scale(${scale})`,
          transformOrigin: "top",
        }}
      />
      <DialogPage />
      <NotificationDetail />
      <ParentGateWrapper />

      {active &&
        location.pathname !== "/" &&
        !location.pathname.includes("/auth") &&
        !location.pathname.includes("/parent") && (
          <>
            <UserBtn />
            <TopTutorial />
            <UniversalBtn />
            <TrialNoticeBalloon />
          </>
        )}

      <AppRoutes />
    </>
  );
};
